import React, { useCallback, useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import MuiBox from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import HomeIcon from "@mui/icons-material/Home";
import PieChartOutlineOutlinedIcon from "@mui/icons-material/PieChartOutlineOutlined";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import MicNoneOutlinedIcon from "@mui/icons-material/MicNoneOutlined";
import Box from "components/common/Box";
import Text from "components/common/Text";
import { config } from "config";
import {
  BOTTOM_NAV_HEIGHT,
  BOTTOM_NAV_ITEMS,
} from "components/allInOneDashboard/constants/bottomNavConfig";
import {
  BRAND_BLUE,
  BRAND_BLUE_DARK,
} from "components/allInOneDashboard/constants/mobileTheme";
import {
  MARKET_PULSE_HUB_PATH,
  UNIFIED_DASHBOARD_PATH,
} from "components/allInOneDashboard/constants/productRouteMap";
import { triggerTabHaptic } from "library/haptics/tabHaptics";

const ACTIVE_BLUE = BRAND_BLUE;
const INACTIVE = "#71717A";

const REDUCED_MOTION_SX = {
  "@media (prefers-reduced-motion: reduce)": {
    transition: "none !important",
    animation: "none !important",
    "&:active": { transform: "none !important" },
  },
};

const TabIcon = ({ icon, active }) => {
  const color = active ? ACTIVE_BLUE : INACTIVE;
  const sx = {
    fontSize: 24,
    color,
    transition: "color 0.2s ease, transform 0.2s ease",
    animation: active ? "aioTabPop 0.28s ease" : "none",
    "@keyframes aioTabPop": {
      "0%": { transform: "scale(1)" },
      "40%": { transform: "scale(1.14)" },
      "100%": { transform: "scale(1)" },
    },
    ...REDUCED_MOTION_SX,
  };

  switch (icon) {
    case "home":
      return active ? <HomeIcon sx={sx} /> : <HomeOutlinedIcon sx={sx} />;
    case "holdings":
      return <PieChartOutlineOutlinedIcon sx={sx} />;
    case "blogs":
      return <ArticleOutlinedIcon sx={sx} />;
    case "marketPulse":
      return (
        <MicNoneOutlinedIcon
          sx={{
            fontSize: 16,
            color: "#fff",
            transition: "transform 0.2s ease",
            animation: active ? "aioTabPop 0.28s ease" : "none",
            "@keyframes aioTabPop": {
              "0%": { transform: "scale(1)" },
              "40%": { transform: "scale(1.14)" },
              "100%": { transform: "scale(1)" },
            },
            ...REDUCED_MOTION_SX,
          }}
        />
      );
    default:
      return null;
  }
};

const scrollContentToTop = () => {
  const scrollingEl = document.scrollingElement || document.documentElement;
  if (typeof window.scrollTo === "function") {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }
  if (scrollingEl) {
    scrollingEl.scrollTop = 0;
  }
};

const AllInOneBottomNav = () => {
  const history = useHistory();
  const location = useLocation();
  const tabRefs = useRef({});
  const rowRef = useRef(null);
  const [indicator, setIndicator] = useState({ left: 0, width: 0, ready: false });
  const isNativeApp = config.isMobileApp;

  const isActive = useCallback(
    (item) => {
      if (item.id === "home") {
        return location.pathname === UNIFIED_DASHBOARD_PATH;
      }
      if (item.id === "marketPulse") {
        return (
          location.pathname === MARKET_PULSE_HUB_PATH ||
          location.pathname.startsWith(`${MARKET_PULSE_HUB_PATH}/`)
        );
      }
      return (
        location.pathname === item.path ||
        location.pathname.startsWith(`${item.path}/`)
      );
    },
    [location.pathname],
  );

  const standardTabs = BOTTOM_NAV_ITEMS.filter((item) => !item.highlighted);
  const highlightedTabs = BOTTOM_NAV_ITEMS.filter((item) => item.highlighted);
  const activeStandardId = standardTabs.find((item) => isActive(item))?.id;

  useEffect(() => {
    if (!isNativeApp) return undefined;

    const updateIndicator = () => {
      if (!activeStandardId || !rowRef.current) {
        setIndicator((prev) => ({ ...prev, ready: false, width: 0 }));
        return;
      }
      const el = tabRefs.current[activeStandardId];
      if (!el) return;
      const rowRect = rowRef.current.getBoundingClientRect();
      const tabRect = el.getBoundingClientRect();
      setIndicator({
        left: tabRect.left - rowRect.left + tabRect.width / 2 - 10,
        width: 20,
        ready: true,
      });
    };

    updateIndicator();
    window.addEventListener("resize", updateIndicator);
    return () => window.removeEventListener("resize", updateIndicator);
  }, [activeStandardId, location.pathname, isNativeApp]);

  const handlePress = (item) => {
    triggerTabHaptic();
  };

  const handleClick = (item) => {
    if (isActive(item)) {
      scrollContentToTop();
      return;
    }
    if (item.path) {
      history.push(item.path);
    }
  };

  // WEB (desktop + mobile-web): no bottom nav. ANDROID / IOS only.
  if (!isNativeApp) {
    return null;
  }

  // Hide bottom nav on subscription/payment screens so fixed CTAs are visible.
  const HIDE_NAV_PATHS = ["/customer/profile/subscription"];
  if (HIDE_NAV_PATHS.some((p) => location.pathname.startsWith(p))) {
    return null;
  }

  return (
    <Box
      component="nav"
      aria-label="Primary"
      sx={{
        display: "block",
        position: "fixed",
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1200,
        boxSizing: "border-box",
        height: `calc(${BOTTOM_NAV_HEIGHT}px + env(safe-area-inset-bottom, 0px))`,
        minHeight: BOTTOM_NAV_HEIGHT,
        pb: "env(safe-area-inset-bottom, 0px)",
        bgcolor: "#fff",
        boxShadow: "0px -4px 19px 0px rgba(0,0,0,0.05)",
      }}
    >
      <MuiBox
        ref={rowRef}
        sx={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          pt: 2,
          gap: 1,
        }}
      >
        {indicator.ready ? (
          <MuiBox
            aria-hidden
            sx={{
              position: "absolute",
              top: 8,
              left: indicator.left,
              width: indicator.width,
              height: 3,
              borderRadius: 2,
              bgcolor: ACTIVE_BLUE,
              transition:
                "left 0.25s cubic-bezier(0.4, 0, 0.2, 1), width 0.25s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.2s ease",
              ...REDUCED_MOTION_SX,
            }}
          />
        ) : null}

        {standardTabs.map((item) => {
          const active = isActive(item);
          return (
            <ButtonBase
              key={item.id}
              ref={(node) => {
                tabRefs.current[item.id] = node;
              }}
              type="button"
              onPointerDown={() => handlePress(item)}
              onClick={() => handleClick(item)}
              aria-label={item.label}
              aria-current={active ? "page" : undefined}
              centerRipple
              TouchRippleProps={{
                style: { color: "rgba(56, 126, 209, 0.28)" },
              }}
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.25,
                border: 0,
                bgcolor: "transparent",
                cursor: "pointer",
                py: 0.5,
                px: 0.5,
                m: 0,
                minWidth: 0,
                minHeight: 48,
                borderRadius: 2,
                overflow: "hidden",
                transition: "transform 0.12s ease",
                "&:active": { transform: "scale(0.96)" },
                ...REDUCED_MOTION_SX,
              }}
            >
              <TabIcon icon={item.icon} active={active} />
              <Text
                sx={{
                  fontSize: 12,
                  fontWeight: active ? 500 : 400,
                  color: active ? ACTIVE_BLUE : INACTIVE,
                  letterSpacing: "-0.32px",
                  lineHeight: "21px",
                  whiteSpace: "nowrap",
                  transition: "color 0.2s ease, font-weight 0.2s ease",
                  ...REDUCED_MOTION_SX,
                }}
              >
                {item.label}
              </Text>
            </ButtonBase>
          );
        })}

        {highlightedTabs.map((item) => {
          const active = isActive(item);
          return (
            <ButtonBase
              key={item.id}
              type="button"
              onPointerDown={() => handlePress(item)}
              onClick={() => handleClick(item)}
              aria-label={item.label}
              aria-current={active ? "page" : undefined}
              centerRipple
              TouchRippleProps={{
                style: { color: "rgba(255, 255, 255, 0.35)" },
              }}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "3px",
                border: 0,
                cursor: "pointer",
                p: 0,
                m: 0,
                height: 48,
                pl: 1.125,
                pr: 1.5,
                borderRadius: "12px",
                background: `linear-gradient(120deg, ${BRAND_BLUE} 1.7%, ${BRAND_BLUE_DARK} 98%)`,
                boxShadow: `0px 0px 4px 0px ${BRAND_BLUE}, inset 0px 3px 10px 0px rgba(3,48,103,0.2)`,
                flexShrink: 0,
                overflow: "hidden",
                transition: "transform 0.12s ease, box-shadow 0.2s ease",
                "&:active": { transform: "scale(0.96)" },
                animation: active ? "aioMpGlow 0.6s ease" : "none",
                "@keyframes aioMpGlow": {
                  "0%": {
                    boxShadow: `0 0 0 0 rgba(56,126,209,0.55), inset 0px 3px 10px 0px rgba(3,48,103,0.2)`,
                  },
                  "100%": {
                    boxShadow: `0 0 0 10px rgba(56,126,209,0), 0px 0px 4px 0px ${BRAND_BLUE}, inset 0px 3px 10px 0px rgba(3,48,103,0.2)`,
                  },
                },
                ...REDUCED_MOTION_SX,
              }}
            >
              <TabIcon icon={item.icon} active={active} />
              <Text
                sx={{
                  fontSize: 12,
                  fontWeight: 400,
                  color: "#fff",
                  letterSpacing: "-0.32px",
                  lineHeight: "14px",
                  textAlign: "left",
                  whiteSpace: "pre-line",
                }}
              >
                {"Market\nPulse"}
              </Text>
            </ButtonBase>
          );
        })}
      </MuiBox>
    </Box>
  );
};

export default AllInOneBottomNav;
